import type { MultiFrameTransition, ReferencePrompt, TaskInfo, TaskParams, TextPromptWithInputItem } from '@/app/types';
import {
  getImageAndImageElementCount,
  getTextElementCount,
  mapTaskTypeToFormTabType,
  stringifyTextPromptWithReference,
} from '@/app/utils';
import type { HomeGenerationMode } from './home-generation-mode';
import { isHomeReferenceMode, isHomeVideoMode } from './home-generation-mode';

export type HomeGenerationType = 'video' | 'image';
export type HomeGenerationMethod = 'reference' | 'image' | 'text';

export const HOME_MULTI_FRAME_DEFAULT_DURATION = 5;
const HOME_MENTIONS_TEXTAREA_ACTION_HEIGHT = 24;

export const getImageAndTextReferenceCount = (referencePrompts: ReferencePrompt[]) =>
  getImageAndImageElementCount(referencePrompts) + getTextElementCount(referencePrompts);

export const getTotalReferenceImageCount = (referencePrompts: ReferencePrompt[]) =>
  referencePrompts.reduce((count, { type, prompt }) => {
    if (type === 'image' && prompt) {
      return count + 1;
    }

    if (type !== 'element' || !prompt || !('components' in prompt) || !Array.isArray(prompt.components)) {
      return count;
    }

    return count + prompt.components.filter((component) => component.content_type === 'image').length;
  }, 0);

export const getHomeMentionsEditorMinHeightClass = (textareaMinHeight: number) => {
  const editorMinHeight = Math.max(textareaMinHeight - HOME_MENTIONS_TEXTAREA_ACTION_HEIGHT, 32);

  if (editorMinHeight <= 32) {
    return '[&_.ProseMirror]:min-h-[32px]';
  }

  if (editorMinHeight <= 72) {
    return '[&_.ProseMirror]:min-h-[72px]';
  }

  return '[&_.ProseMirror]:min-h-[116px]';
};

export const getSubmitTaskType = (
  mode: HomeGenerationMode,
  validImageCount: number,
): Exclude<TaskParams['type'], 'upscale'> => {
  if (mode !== 'img2video') {
    return mode;
  }

  if (validImageCount === 2) {
    return 'headtailimg2video';
  }

  if (validImageCount > 2) {
    return 'multi_frame';
  }

  return 'img2video';
};

export const getHomeMultiFrameDurationLabel = (transition?: MultiFrameTransition) => {
  const duration = transition?.duration ?? HOME_MULTI_FRAME_DEFAULT_DURATION;
  return `${duration}s`;
};

export const getHomeMultiFramePrompt = (transition?: MultiFrameTransition) => transition?.prompt || '';

export const hasReferenceText = (referencePrompts: ReferencePrompt[], textPrompt: TextPromptWithInputItem) =>
  stringifyTextPromptWithReference(textPrompt).trim().length > 0 || referencePrompts.length > 0;

export const getHomeGenerationType = (mode: HomeGenerationMode): HomeGenerationType =>
  isHomeVideoMode(mode) ? 'video' : 'image';

export const getHomeGenerationMethod = (mode: HomeGenerationMode): HomeGenerationMethod => {
  if (isHomeReferenceMode(mode)) {
    return 'reference';
  }

  return mode === 'img2video' ? 'image' : 'text';
};

export const getHomeModeFromGeneration = (
  type: HomeGenerationType,
  method: HomeGenerationMethod,
): HomeGenerationMode | undefined => {
  if (type === 'video') {
    const modeByMethod: Record<HomeGenerationMethod, HomeGenerationMode> = {
      reference: 'character2video',
      image: 'img2video',
      text: 'text2video',
    };

    return modeByMethod[method];
  }

  if (method === 'reference') {
    return 'reference2image';
  }

  if (method === 'text') {
    return 'text2image';
  }

  return undefined;
};

export const getHomeGenerationMethods = (type: HomeGenerationType): HomeGenerationMethod[] =>
  type === 'video' ? ['reference', 'image', 'text'] : ['reference', 'text'];

const HOME_GENERATION_TYPE_ORDER: HomeGenerationType[] = ['video', 'image'];

/**
 * 桌面端「类型 + 方式」合并下拉的全部有效组合，按 类型 → 方式 顺序铺平。
 *
 * 组合是笛卡尔积的子集：image 类型没有 img2image，所以只能由
 * getHomeGenerationMethods + getHomeModeFromGeneration 推导，不能硬编码。
 */
export const getHomeGenerationModeOptions = (): Array<{
  method: HomeGenerationMethod;
  mode: HomeGenerationMode;
  type: HomeGenerationType;
}> =>
  HOME_GENERATION_TYPE_ORDER.flatMap((type) =>
    getHomeGenerationMethods(type).flatMap((method) => {
      const mode = getHomeModeFromGeneration(type, method);
      return mode ? [{ method, mode, type }] : [];
    }),
  );

export const getHomeSubmittedTaskRoute = (
  taskType: TaskInfo['type'] | undefined,
  fallbackTaskType?: TaskInfo['type'],
) => {
  const formTabType = mapTaskTypeToFormTabType(taskType);
  if (formTabType) {
    return `/create/${formTabType}`;
  }

  const fallbackFormTabType = fallbackTaskType ? mapTaskTypeToFormTabType(fallbackTaskType) : null;
  return fallbackFormTabType ? `/create/${fallbackFormTabType}` : '/assets/mycreations';
};
